import { Link } from "react-router-dom";
import { Seo } from "../lib/seo";
import { isTransparencyInForce, monthsUntilHighRisk } from "../lib/aiAct";

const regulations = [
  {
    key: "ai-act",
    name: "EU AI Act",
    status: "Art. 50 in force since 2 Aug 2026 · high-risk from",
    statusValue: "2 December 2027",
    note: (
      <>
        Regulation (EU) 2024/1689, as amended by Regulation (EU) 2026/1744 (the Digital
        Omnibus on AI, in force 27 July 2026). The omnibus deferred the Chapter III
        high-risk regime for Annex III systems from 2 August 2026 to 2 December 2027, and
        for Annex I systems to 2 August 2028. It did not defer the Article 50 transparency
        duties, which bind a customer-facing assistant today.
      </>
    ),
    bancony: [
      <><strong>Article 50 transparency</strong> built into the Assistant UI. This is the obligation that applies to a customer-facing assistant today.</>,
      <><strong>Article 4 AI literacy</strong> materials for bank staff. The omnibus softened Article 4 to a duty to take measures supporting AI literacy rather than to ensure a given level; it still binds every deployer.</>,
      <><strong>Article 12-15 logging</strong> built into Server and Assistant via Langfuse.</>,
      <><strong>Article 14 human oversight</strong> primitives for irreversible actions.</>,
      <><strong>Article 17 QMS documentation</strong> where the bank operates a high-risk system.</>,
      <><strong>DPIA template</strong> for high-risk classification cases.</>,
    ],
    bank: [
      <>Risk classification of specific use cases. Most assistant scopes sit below the high-risk threshold; Annex III 5(b) catches evaluation of creditworthiness and credit scoring, including where the system only informs a human decision.</>,
      <>Regulator notifications, where scope requires them.</>,
      <>The conformity assessment process for any high-risk AI system the bank operates, against the 2 December 2027 date.</>,
      <>The bank's internal AI literacy programme (we provide the materials; the bank runs the training).</>,
    ],
  },
  {
    key: "dora",
    name: "DORA",
    status: "Applies since",
    statusValue: "17 January 2025",
    bancony: [
      <><strong>Article 28-29 Register of Information</strong>: pre-formatted RoI entry covering Server (and Assistant, where applicable).</>,
      <><strong>Article 30 mandatory contract clauses</strong>: audit rights, exit strategies, sub-outsourcing register, data location, all in Bancony's standard contract.</>,
      <><strong>Article 17 incident classification</strong>: built-in hooks aligned to ESA RTS criteria.</>,
      <><strong>Article 11 operational continuity</strong>: documented RPO and RTO per deployment mode.</>,
      <><strong>Annual self-assessment</strong> published to customers, with remediations tracked.</>,
    ],
    bank: [
      <>Maintaining the bank's overall DORA programme; Bancony slots into it as a critical third-party.</>,
      <>Internal incident reporting workflows and supervisor-facing notifications.</>,
      <>Vendor concentration assessments under DORA Article 29.</>,
    ],
  },
  {
    key: "gdpr",
    name: "GDPR",
    status: "Status",
    statusValue: "In force",
    bancony: [
      <><strong>Data minimisation</strong>: only the data needed for the conversation enters the model context.</>,
      <><strong>Article 6/7 consent</strong>: explicit consent flow for user memory, with a customer-facing setting to manage it.</>,
      <><strong>Article 22</strong>: Assistant does not make consequential decisions autonomously; human-checkpoint primitives by default.</>,
      <><strong>Article 32 security</strong>: encryption in transit and at rest, key management, access controls.</>,
      <><strong>DPA available</strong> on request.</>,
    ],
    bank: [
      <>Lawful basis selection for each processing purpose.</>,
      <>Privacy notice copy in the bank's customer-facing channels.</>,
      <>Data subject request workflows (we provide the tools to fulfil them).</>,
    ],
  },
];

export const Compliance = () => (
  <>
    <Seo
      title="Compliance"
      description="What Bancony delivers and what the bank still owns under the EU AI Act, DORA, and GDPR. Sober, factual, complete."
      path="/compliance"
    />

    <section className="page-hero">
      <div className="page-hero-inner">
        <span className="section-eyebrow">Compliance</span>
        <h1>What we deliver. What you still own.</h1>
        <p className="page-hero-sub">
          This page is for the compliance officer, DPO, and CISO who reviews after the
          decision-maker has decided. It lays out, regulation by regulation, what
          Bancony ships and what the bank is still on the hook for, because the second
          list is the one that matters.
        </p>
      </div>
    </section>

    <section className="section">
      <div className="section-inner">
        {regulations.map((r) => (
          <div className="regulation" key={r.key} id={r.key}>
            <div className="regulation-head">
              <h3>{r.name}</h3>
              <span className="regulation-status">
                {r.status} <strong>{r.statusValue}</strong>
              </span>
            </div>
            {r.key === "ai-act" && (
              <div className="regulation-callout">
                <span className="regulation-callout-text">
                  {isTransparencyInForce()
                    ? `Article 50 has applied to banking chatbots since 2 August 2026. The high-risk rules follow on 2 December 2027, ${monthsUntilHighRisk()} months out.`
                    : "Article 50 applies to banking chatbots from 2 August 2026."}
                </span>
                <Link to="/ai-act-august-2026" className="regulation-callout-link">
                  What applies now →
                </Link>
              </div>
            )}
            {r.note && <p className="regulation-note">{r.note}</p>}
            <div className="regulation-split">
              <div className="regulation-block">
                <h4>What Bancony does</h4>
                <ul>
                  {r.bancony.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              </div>
              <div className="regulation-block">
                <h4>What the bank still owns</h4>
                <ul>
                  {r.bank.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>

    <section className="section soft">
      <div className="section-inner">
        <span className="section-eyebrow">Certifications</span>
        <h2>What we have, honestly.</h2>
        <p className="lede" style={{ maxWidth: 720 }}>
          We will publish SOC 2 Type II and ISO 27001 status on this page once the
          attestations are real. Until then, what we offer is the deployment-mode
          controls below, and the auditable trail every conversation produces.
        </p>
        <div className="residency">
          <span className="residency-pill">EEA-only by default</span>
          <p>
            All hosted data is stored in the EU/EEA. Customers can pin a specific region
            for SaaS deployments. Bank-cloud and on-prem deployments keep all customer
            data inside the bank's perimeter; Bancony never sees it.
          </p>
        </div>
      </div>
    </section>

    <section className="section">
      <div className="section-inner">
        <div className="final-cta">
          <div>
            <h2>Request our compliance pack.</h2>
            <p>DPA, Article 30 clauses, RoI entry template, DPIA template, and current security posture.</p>
          </div>
          <div className="final-cta-cta">
            <Link to="/contact?intent=compliance-pack" className="btn btn-primary">Request the pack</Link>
          </div>
        </div>
      </div>
    </section>
  </>
);
